import {
  Button,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from "@nexus/ui";
import { useId, type KeyboardEvent } from "react";
import { HiddenByFilterNote } from "@/features/marketplace/components/HiddenByFilterNote";
import { useTranslation } from "react-i18next";

import { CopyableId } from "@/features/marketplace/components/orders-list/CopyableId";
import { OrderDeadlineRollup } from "@/features/marketplace/components/orders-list/OrderDeadlineRollup";
import { OrderStatusRollup } from "@/features/marketplace/components/orders-list/OrderStatusRollup";
import { OrderTypeBadge } from "@/features/marketplace/components/orders-list/OrderTypeBadge";
import { localTodayYmd } from "@/features/marketplace/lib/order-deadline";
import { formatOrderLineCount } from "@/features/marketplace/lib/order-line-count-label";
import {
  describeDeadlineRollup,
  describeOrderAttention,
  describeStatusRollup,
} from "@/features/marketplace/lib/orders-list-model";
import { ORDERS_OVERDUE_ROW_CLASS } from "@/features/marketplace/lib/orders-overdue-row";
import { ClientMark } from "@/features/marketplace/partner/components/ClientMark";
import {
  PARTNER_QUEUE_CELL_DUE_CLASS,
  PARTNER_QUEUE_CELL_RECEIVED_CLASS,
  PARTNER_QUEUE_CELL_SCRIPTS_CLASS,
  PARTNER_QUEUE_CELL_STATUS_CLASS,
  PARTNER_QUEUE_CELL_TYPE_CLASS,
  PARTNER_QUEUE_GRID_CLASS,
  PARTNER_QUEUE_ROW_PADDING_CLASS,
} from "@/features/marketplace/partner/lib/partner-queue-grid";
import { PartnerScriptRow } from "@/features/marketplace/partner/components/PartnerScriptRow";
import type { OrderLineVideo } from "@/features/marketplace/types/marketplace.types";
import type {
  PartnerQueueDensity,
  PartnerWorkGroup,
  PartnerWorkItem,
} from "@/features/marketplace/partner/lib/partner-work-queue";
import {
  formatMarketplaceSentAt,
  formatMarketplaceSentColumn,
} from "@/features/marketplace/lib/format-marketplace-timestamp";

interface PartnerOrderRowProps {
  group: PartnerWorkGroup;
  expanded: boolean;
  selectedScriptId: string | null;
  selectedVideoKey: string | null;
  density: PartnerQueueDensity;
  activeRowId: string | null;
  rowIndex: number;
  expandedScriptVideoIds: ReadonlySet<string>;
  /** Opens every line and video of the order the Status filter hid. */
  onRevealStatusHidden?: () => void;
  onToggleExpanded: () => void;
  onToggleScriptVideosExpanded: (scriptId: string) => void;
  onOpen: (item: PartnerWorkItem) => void;
  onOpenForUpload?: (item: PartnerWorkItem, video?: OrderLineVideo) => void;
}

function formatCreatedAt(iso: string, locale: string): string {
  return formatMarketplaceSentAt(iso, locale);
}

function formatCreatedStamp(
  iso: string,
  locale: string,
  now = new Date(),
): string {
  const date = new Date(iso);
  const sameYear = date.getFullYear() === now.getFullYear();
  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    ...(sameYear ? {} : { year: "numeric" }),
    hour: "numeric",
    minute: "2-digit",
    timeZone: "UTC",
    timeZoneName: "short",
  }).format(date);
}

export function PartnerOrderRow({
  group,
  expanded,
  selectedScriptId,
  selectedVideoKey,
  density,
  activeRowId,
  rowIndex,
  expandedScriptVideoIds,
  onRevealStatusHidden,
  onToggleExpanded,
  onToggleScriptVideosExpanded,
  onOpen,
  onOpenForUpload,
}: PartnerOrderRowProps) {
  const { t, i18n } = useTranslation();
  const panelId = useId();
  const todayYmd = localTodayYmd();
  const visibleOrder = {
    ...group.order,
    scripts: group.items.map((item) => item.script),
  };
  const statusRollup = describeStatusRollup(visibleOrder);
  const deadlineRollup = describeDeadlineRollup(visibleOrder, todayYmd);
  const isOverdue = describeOrderAttention(visibleOrder, todayYmd).overdue;
  const clientName = group.order.tenant.name;
  const sentIso = group.order.sentAt ?? group.order.createdAt;

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter") {
      event.preventDefault();
      onToggleExpanded();
    } else if (event.key === "ArrowRight" && !expanded) {
      event.preventDefault();
      onToggleExpanded();
    } else if (event.key === "ArrowLeft" && expanded) {
      event.preventDefault();
      onToggleExpanded();
    }
  };

  return (
    <li role="presentation">
      <div
        id={`partner-order-${group.order.id}`}
        role="row"
        aria-level={1}
        aria-rowindex={rowIndex}
        data-partner-order-row
        data-overdue={isOverdue ? "true" : undefined}
        data-tree-row
        tabIndex={activeRowId === `partner-order-${group.order.id}` ? 0 : -1}
        aria-expanded={expanded}
        aria-controls={panelId}
        aria-label={t("marketplace.partner.list.rowLabel", {
          client: clientName,
          count: group.items.length,
        })}
        onClick={onToggleExpanded}
        onKeyDown={handleKeyDown}
        className={cn(
          PARTNER_QUEUE_GRID_CLASS,
          PARTNER_QUEUE_ROW_PADDING_CLASS,
          "group/row scroll-mt-20 cursor-pointer text-left outline-none transition-colors duration-150 ease-out",
          density === "compact" ? "min-h-13 py-1.5" : "min-h-16 py-2.5",
          "focus-visible:bg-muted/60 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/50",
          selectedScriptId
            ? "bg-primary-subtle/40"
            : "bg-card hover:bg-muted/30",
          isOverdue && ORDERS_OVERDUE_ROW_CLASS,
        )}
      >
        <span role="gridcell" className="flex items-center">
          <button
            type="button"
            aria-expanded={expanded}
            aria-label={t(
              expanded
                ? "marketplace.partner.list.collapseOrder"
                : "marketplace.partner.list.expandOrder",
              { client: clientName },
            )}
            className="flex size-7 -ml-1.5 cursor-pointer items-center justify-center rounded-sm text-muted-foreground/70 transition-colors hover:bg-muted hover:text-foreground"
            onClick={(event) => {
              event.stopPropagation();
              onToggleExpanded();
            }}
          >
            <Icon
              name="chevron-right"
              size="sm"
              color="inherit"
              className={cn(
                "transition-transform duration-200 ease-out motion-reduce:transition-none",
                expanded && "rotate-90",
              )}
              aria-hidden
            />
          </button>
        </span>

        <div role="gridcell" className="flex min-w-0 items-center gap-2.5">
          <span aria-hidden>
            <ClientMark
              name={clientName}
              logoUrl={group.order.tenant.logoUrl}
              brandColor={group.order.tenant.brandColor}
              size="sm"
            />
          </span>
          <span className="min-w-0">
            <span className="block min-w-0 truncate text-sm font-semibold text-foreground">
              {clientName}
            </span>
            <span className="mt-0.5 flex min-w-0 items-center gap-1.5 text-caption text-muted-foreground">
              <CopyableId
                value={group.order.id}
                displayValue={group.orderRef}
                density="quiet"
                copyLabel={t("marketplace.partner.copyOrderRef")}
                copiedMessage={t("marketplace.partner.orderRefCopied")}
                className="min-w-0"
              />
              <span aria-hidden className="shrink-0 text-muted-foreground/40">
                ·
              </span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <time
                    dateTime={group.order.createdAt}
                    className="shrink-0 tabular-nums"
                  >
                    {formatCreatedStamp(group.order.createdAt, i18n.language)}
                  </time>
                </TooltipTrigger>
                <TooltipContent>
                  {t("marketplace.orders.list.created", {
                    date: formatCreatedAt(group.order.createdAt, i18n.language),
                  })}
                </TooltipContent>
              </Tooltip>
            </span>
            <span className="mt-1 flex flex-wrap items-center gap-2 sm:hidden">
              <OrderStatusRollup rollup={statusRollup} />
              <OrderDeadlineRollup
                rollup={deadlineRollup}
                todayYmd={todayYmd}
                workingWeekdays={group.order.vendor.workingWeekdays}
                createdAt={group.order.createdAt}
              />
            </span>
          </span>
        </div>

        <span role="gridcell" className={PARTNER_QUEUE_CELL_TYPE_CLASS}>
          <OrderTypeBadge type={group.orderType} />
        </span>

        <span
          role="gridcell"
          className={cn(
            PARTNER_QUEUE_CELL_SCRIPTS_CLASS,
            "text-xs tabular-nums text-muted-foreground",
          )}
        >
          {formatOrderLineCount(visibleOrder.scripts, t)}
        </span>

        <span role="gridcell" className={PARTNER_QUEUE_CELL_STATUS_CLASS}>
          <OrderStatusRollup rollup={statusRollup} />
        </span>

        <time
          role="gridcell"
          dateTime={sentIso}
          className={cn(
            PARTNER_QUEUE_CELL_RECEIVED_CLASS,
            "text-xs tabular-nums text-muted-foreground",
          )}
        >
          {formatMarketplaceSentColumn(sentIso, i18n.language)}
        </time>

        <span role="gridcell" className={PARTNER_QUEUE_CELL_DUE_CLASS}>
          <OrderDeadlineRollup
            rollup={deadlineRollup}
            todayYmd={todayYmd}
            workingWeekdays={group.order.vendor.workingWeekdays}
            createdAt={group.order.createdAt}
          />
        </span>

        <span role="gridcell" className="flex items-center justify-end">
          {group.briefUrl ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 text-xs"
              asChild
            >
              <a
                href={group.briefUrl}
                target="_blank"
                rel="noreferrer"
                onClick={(event) => event.stopPropagation()}
              >
                {t("marketplace.partner.briefLink", "Brief")}
              </a>
            </Button>
          ) : null}
        </span>
      </div>
      {expanded ? (
        <ul id={panelId} role="presentation">
          {group.items.map((item, index) => {
            const scriptRowIndex = rowIndex + index + 1;
            const videosExpanded = expandedScriptVideoIds.has(item.script.id);
            return (
              <PartnerScriptRow
                key={item.script.id}
                item={item}
                index={index + 1}
                density={density}
                highlighted={selectedScriptId === item.script.id}
                matchesStatusFilter={item.matchesStatusFilter}
                onRevealStatusHidden={onRevealStatusHidden}
                tabIndex={
                  activeRowId ===
                  `partner-work-${item.order.id}-${item.script.id}`
                    ? 0
                    : -1
                }
                rowIndex={scriptRowIndex}
                videosExpanded={videosExpanded}
                onToggleVideosExpanded={() =>
                  onToggleScriptVideosExpanded(item.script.id)
                }
                onOpen={onOpen}
                onOpenForUpload={onOpenForUpload}
                selectedVideoKey={
                  selectedScriptId === item.script.id ? selectedVideoKey : null
                }
              />
            );
          })}
          {group.statusHiddenItems.length > 0 ? (
            <li role="presentation">
              <HiddenByFilterNote
                kind="lines"
                count={group.statusHiddenItems.length}
                onReveal={onRevealStatusHidden}
              />
            </li>
          ) : null}
        </ul>
      ) : null}
    </li>
  );
}
